"use client";

import CodeMirror from "@uiw/react-codemirror";
import { githubDark } from "@uiw/codemirror-theme-github";
import { javascript } from "@codemirror/lang-javascript";
import PeerquikCTA from "@/app/components/PeerquikCTA";
import Image from "next/image";
import Link from "next/link";
import ExamplesShowcase from "@/app/components/ExamplesShowcase";

const Page = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <section className="mb-8">
        <h1 className="text-3xl font-bold mb-4">
          Best Next.js Boilerplate for B2C Marketplaces in 2025
        </h1>
        <p className="text-md mb-4">
          Building a B2C marketplace in 2025? You need a boilerplate that saves time, scales well, and boosts your SEO. Meet <strong>PeerQuik</strong>—a full-featured{" "}
          <Link className="underline text-cyan-400 hover:text-cyan-500" href="https://peerquik.com">Next.js boilerplate</Link> for B2C marketplaces, packed with everything to launch fast and rank high. Pair it with <Link className="underline text-cyan-400 hover:text-cyan-500" href="https://seozast.com">SEOZast</Link>’s AI-driven content, and your MVP—like <Link className="underline text-cyan-400 hover:text-cyan-500" href="https://www.beunderwater.com">Be Underwater</Link>—will hit the ground running. See why it’s top-tier in <Link className="underline text-cyan-400 hover:text-cyan-500" href="/articles/best-nextjs-boilerplate-for-b2c-marketplaces-2025">Best Next.js Boilerplate for B2C Marketplaces in 2025</Link>.
        </p>
      </section>

      <div className="mt-6 mb-6 flex justify-center">
        <figure>
          <Image
            src="/images/peerquik-photo.png"
            alt="The Ultimate Next.js Boilerplate for B2C Marketplaces"
            width={400}
            height={238}
            priority
            loading="eager"
            className="rounded-lg mb-2"
          />
          <figcaption className="text-sm text-center text-gray-300">
            The Ultimate Next.js Boilerplate for B2C Marketplaces
          </figcaption>
        </figure>
      </div>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          Why PeerQuik Is the Best Next.js Boilerplate for B2C Marketplaces
        </h2>
        <p className="text-md mb-4">
          Unlike basic boilerplates, PeerQuik delivers every critical feature for a real-world B2C marketplace:
        </p>
        <ul className="list-disc pl-6 text-md mb-4">
          <li><strong>Next.js (App Router + TypeScript)</strong>: Modern, scalable architecture.</li>
          <li><strong>Prisma + Postgres/Supabase schema</strong>: Robust data modeling.</li>
          <li><strong>Dual auth for Buyers & Sellers</strong>: Email + Google OAuth.</li>
          <li><strong>Password reset, email verification, change email</strong>: Built-in flows.</li>
          <li><strong>Admin, Buyer, Seller, Listings, Reviews routes</strong>: All pre-configured.</li>
          <li><strong>Admin dashboard</strong>: Fully functional for managing users and listings.</li>
          <li><strong>Newsletter footer</strong>: Plug-and-play for growth.</li>
          <li><strong>Reviews + Ratings</strong>: Logic + UI included.</li>
          <li><strong>Navbar with search logic</strong>: Optimized for discovery.</li>
          <li><strong>Custom reusable hooks</strong>: Write less code, faster iterations.</li>
          <li><strong>Currency converter</strong>: Essential for international marketplaces.</li>
          <li><strong>Cron jobs</strong>: Handle recurring tasks seamlessly.</li>
          <li><strong>CAPTCHA on forms</strong>: Security out of the box.</li>
          <li><strong>Component library</strong>: Modals, buttons, forms, headings, maps, etc.</li>
          <li><strong>Favorites system</strong>: Buyers can bookmark listings/sellers.</li>
          <li><strong>Stripe Subscriptions</strong>: Premium seller plans + invoicing + emails.</li>
          <li><strong>SEO tools</strong>: Sitemap generator, robots.txt, PSEO folder included.</li>
          <li><strong>ChatGPT prompt files</strong>: Terms, privacy, data deletion pages auto-generated.</li>
          <li><strong>README for easy setup</strong>: Clear instructions.</li>
          <li><strong>Docker setup</strong>: Ready for Coolify or VPS deployment.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          Built for Modern B2C Startups
        </h2>
        <p className="text-md mb-4">
          PeerQuik was crafted by indie makers for indie makers, with a stack that reflects modern best practices and features tailored for B2C platforms. Whether it’s a local marketplace or a global booking system, this boilerplate saves hundreds of hours. For indie hackers, it’s a no-brainer—check out <Link className="underline text-cyan-400 hover:text-cyan-500" href="/articles/best-saas-boilerplate-for-indie-hackers">Best SaaS Boilerplate for Indie Hackers</Link>.
        </p>
        <p className="text-md mb-4">
          PeerQuik isn’t just UI and routes—it’s real logic and working examples for B2C use cases, designed for rapid iteration.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          Example: How Stripe Premium Subscriptions Work
        </h2>
        <p className="text-md mb-4">
          Want to monetize your marketplace? PeerQuik’s Stripe integration handles premium seller subscriptions, invoices, and role-based access. Here’s a simplified example:
        </p>
        <CodeMirror
          value={`import { stripe } from "@/lib/stripe";
import { getUser } from "@/lib/auth";
import { createSubscription } from "@/lib/db";

export async function POST(req: Request) {
  const user = await getUser(req);
  if (!user) return new Response("Unauthorized", { status: 401 });

  const session = await stripe.checkout.sessions.create({
    customer_email: user.email,
    line_items: [
      {
        price: process.env.STRIPE_PREMIUM_PRICE_ID!,
        quantity: 1,
      },
    ],
    mode: "subscription",
    success_url: \`\${process.env.NEXT_PUBLIC_URL}/dashboard?success=true\`,
    cancel_url: \`\${process.env.NEXT_PUBLIC_URL}/dashboard?canceled=true\`,
  });

  await createSubscription(user.id, session.id);
  return new Response(JSON.stringify({ url: session.url }));
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="300px"
          className="mb-4"
        />
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          SEO-Ready with PSEO in Mind
        </h2>
        <p className="text-md mb-4">
          PeerQuik’s <strong>PSEO folder</strong>, dynamic <code>sitemap.xml</code> generator, and <code>robots.txt</code> route are built for crawling. Targeting long-tail traffic (e.g., “plumber in Miami” or “best dive center in Bali”)? You can generate optimized pages instantly. Learn more in <Link className="underline text-cyan-400 hover:text-cyan-500" href="/articles/pseo-nextjs-peerquik">PSEO with Next.js and Peerquik</Link>.
        </p>
        <p className="text-md mb-4">
          With SEOZast’s $5/month AI content and PeerQuik’s modular routes, your marketplace scales SEO effortlessly.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          Who Is PeerQuik For?
        </h2>
        <ul className="list-disc pl-6 text-md mb-4">
          <li>Solo developers launching MVPs fast.</li>
          <li>Startups building two-sided marketplaces.</li>
          <li>Indie makers building SaaS with buyers/sellers.</li>
          <li>Agencies needing repeatable codebases.</li>
        </ul>
        <p className="text-md mb-4">
          If you’re building a B2C product and don’t want to reinvent auth, admin panels, or Stripe logic—PeerQuik is for you.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          How to Get Started
        </h2>
        <p className="text-md mb-4">
          PeerQuik includes a <code>README.md</code> and Docker setup. Run <code>docker-compose up</code>, and you’re live locally with auth, search, admin dashboard, and SEO tools.
        </p>
        <p className="text-md mb-4">
          Ready to launch your next marketplace? <PeerquikCTA />
        </p>
      </section>

      <ExamplesShowcase />
    </div>
  );
};

export default Page;